/**
 * usePlaybackResume.ts — хук сохранения и восстановления позиции воспроизведения.
 *
 * Сохраняет текущую позицию в localStorage каждые 5 секунд и при паузе/покидании страницы.
 * При загрузке метаданных видео автоматически восстанавливает позицию, если она была сохранена.
 * Возвращает `resumedFrom` — секунду, с которой было продолжено воспроизведение (null = начало).
 */

import { useEffect, useRef, useState } from 'react';

const SAVE_INTERVAL_MS = 5_000;
const MIN_RESUME_SECONDS = 5;   // не восстанавливать если осталось меньше 5 секунд от начала
const NEAR_END_THRESHOLD = 10;  // считать «конец видео» если осталось меньше 10 секунд

function storageKey(videoId: string): string {
  return `abr-resume-${videoId}`;
}

function safeStorageGet(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeStorageSet(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Storage can be unavailable in Safari private/restricted modes.
  }
}

function safeStorageRemove(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Ignore storage failures; playback should continue.
  }
}

interface UsePlaybackResumeResult {
  /** Время (сек) с которого было продолжено воспроизведение, null если началось сначала */
  resumedFrom: number | null;
  /** Сбросить индикатор "продолжаю с..." вручную */
  clearResumedFrom: () => void;
}

export function usePlaybackResume(
  videoRef: React.RefObject<HTMLVideoElement>,
  videoId: string | null | undefined
): UsePlaybackResumeResult {
  const [resumedFrom, setResumedFrom] = useState<number | null>(null);
  const saveTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  // Флаг: восстановление уже выполнено для текущего videoId
  const restoredRef = useRef(false);

  useEffect(() => {
    if (!videoId || !videoRef.current) return;
    restoredRef.current = false;

    const video = videoRef.current;

    // ── Восстановление позиции при загрузке метаданных ───────────────────────
    const handleLoadedMetadata = () => {
      if (restoredRef.current) return;
      restoredRef.current = true;

      const saved = safeStorageGet(storageKey(videoId));
      if (!saved) return;

      const savedTime = parseFloat(saved);
      if (isNaN(savedTime) || savedTime < MIN_RESUME_SECONDS) return;

      // Не восстанавливать если почти в конце
      if (video.duration > 0 && savedTime >= video.duration - NEAR_END_THRESHOLD) {
        safeStorageRemove(storageKey(videoId));
        return;
      }

      video.currentTime = savedTime;
      setResumedFrom(savedTime);
    };

    // ── Сохранение позиции по таймеру ────────────────────────────────────────
    const savePosition = () => {
      if (!video.duration || video.ended) return;
      const remaining = video.duration - video.currentTime;
      if (remaining < NEAR_END_THRESHOLD) {
        // Видео почти закончилось — удаляем сохранённую позицию
        safeStorageRemove(storageKey(videoId));
        return;
      }
      if (video.currentTime > MIN_RESUME_SECONDS) {
        safeStorageSet(storageKey(videoId), String(video.currentTime));
      }
    };

    // ── Очистка при окончании видео ──────────────────────────────────────────
    const handleEnded = () => {
      safeStorageRemove(storageKey(videoId));
      setResumedFrom(null);
    };

    // Сохранять по таймеру пока идёт воспроизведение
    saveTimer.current = setInterval(savePosition, SAVE_INTERVAL_MS);

    // Сохранять при паузе и покидании страницы
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('pause', savePosition);
    video.addEventListener('ended', handleEnded);
    window.addEventListener('beforeunload', savePosition);

    return () => {
      if (saveTimer.current) clearInterval(saveTimer.current);
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('pause', savePosition);
      video.removeEventListener('ended', handleEnded);
      window.removeEventListener('beforeunload', savePosition);
    };
  }, [videoId, videoRef]);

  return {
    resumedFrom,
    clearResumedFrom: () => setResumedFrom(null),
  };
}
